import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Event as EventIcon,
  MusicNote as MusicIcon,
  Star as StarIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowIcon
} from "@mui/icons-material";
import { getServiceBySlug } from "../data/servicesData";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

const iconMap = {
  music: <MusicIcon sx={{ fontSize: 40 }} />,
  event: <EventIcon sx={{ fontSize: 40 }} />,
  star: <StarIcon sx={{ fontSize: 40 }} />,
  group: <GroupIcon sx={{ fontSize: 40 }} />
};

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();
  const service = getServiceBySlug(serviceSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  if (!service) {
    return (
      <>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4">Service not found</Typography>
          <Button component={Link} to="/" sx={{ mt: 2 }}>Return Home</Button>
        </Container>
        <Footer />
      </>
    );
  }


  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="keywords" content={service.keywords} />
        <link rel="canonical" href={`https://imperialdrummers.com/services/${service.slug}`} />

        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:url" content={`https://imperialdrummers.com/services/${service.slug}`} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": service.title,
            "description": service.metaDescription,
            "url": `https://imperialdrummers.com/services/${service.slug}`,
            "provider": {
              "@type": "Organization",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com"
            },
            "serviceType": service.title,
            "areaServed": {
              "@type": "Place",
              "name": "Worldwide"
            }
          })}
        </script>

        {service.faqs && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": service.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}
          </script>
        )}

        {service.process && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "name": `How to Book ${service.title} with Imperial Drummers`,
              "description": `Step-by-step guide to booking professional ${service.title.toLowerCase()} with Imperial Drummers.`,
              "step": service.process.map(step => ({
                "@type": "HowToStep",
                "name": step.title,
                "text": step.description,
                "position": step.step
              }))
            })}
          </script>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": "https://imperialdrummers.com/services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": service.title,
                "item": `https://imperialdrummers.com/services/${service.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '75vh' },
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 14, md: 12 },
          pb: { xs: 8, md: 10 },
        }}>
          {/* Sophisticated Overlay System */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.4) 100%)',
            zIndex: 1
          }} />

          {/* Vignette effect */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            boxShadow: 'inset 0 0 150px rgba(0,0,0,0.5)',
            zIndex: 1,
            pointerEvents: 'none'
          }} />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box className="hero-stagger hero-stagger-left" sx={{ animationDuration: '0.5s', animationDelay: '0s' }}>
                  <Chip
                    label={service.hero.badge}
                    sx={{
                      mb: 2,
                      backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      color: '#ffd700',
                      fontWeight: 800,
                      fontSize: '0.8rem',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 215, 0, 0.3)'
                    }}
                  />
                </Box>

                <Box className="hero-stagger hero-stagger-left" sx={{ animationDuration: '0.5s', animationDelay: '0.1s' }}>
                  <Typography
                    variant={isMobile ? "h4" : "h2"}
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      textShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      fontFamily: '"EB Garamond SC", serif',
                      lineHeight: 1.1,
                      fontSize: { md: '3.5rem' }
                    }}
                  >
                    {service.hero.heading}
                  </Typography>
                </Box>

                <Box className="hero-stagger hero-stagger-left" sx={{ animationDuration: '0.5s', animationDelay: '0.2s' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      opacity: 0.9,
                      lineHeight: 1.7,
                      fontWeight: 400,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}
                  >
                    {service.hero.subheading}
                  </Typography>
                </Box>

                <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.3s' }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={scrollToBooking}
                      startIcon={<EventIcon />}
                      color="gold"
                      sx={{
                        px: 5,
                        py: 2,
                        boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3)',
                        '&:hover': {
                          boxShadow: '0 12px 30px rgba(255, 215, 0, 0.4)',
                        }
                      }}
                    >
                      Get Free Quote
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/', { state: { scrollTo: 'portfolio-section' } })}
                      sx={{
                        px: 5,
                        py: 2,
                        borderColor: 'rgba(255,255,255,0.5)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      View Our Work
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box className="hero-stagger hero-stagger-scale" sx={{ animationDuration: '0.5s', animationDelay: '0.4s' }}>
                  <Paper sx={{
                    p: 4,
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(15px)',
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.15)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                  }}>
                    <Typography variant="h6" sx={{ color: '#ffd700', mb: 3, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.9rem' }}>
                      Why Choose Imperial?
                    </Typography>
                    <List dense>
                      {[
                        "2,000+ events performed worldwide",
                        "15+ years professional experience",
                        "Luxury outfit options available",
                        "Flexible packages for all budgets",
                        "UK-wide & international coverage"
                      ].map((item, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon sx={{ color: '#ffd700', fontSize: 22 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ sx: { color: 'white', fontWeight: 500, fontSize: '1rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Intro / About Section */}
        <Box sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={8} sx={{ mx: 'auto' }}>
                <Typography variant="h2" sx={{ color: '#5C005D', mb: 3, fontSize: { xs: '2rem', md: '3rem' } }}>
                  {service.intro.heading}
                </Typography>
                {service.intro.paragraphs.map((para, i) => (
                  <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontSize: '1.1rem' }}>
                    {para}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services / What We Offer */}
        <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              What We Offer
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Professional dhol entertainment for every moment of your celebration
            </Typography>

            <Grid container spacing={4}>
              {service.services.map((item, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{
                    height: '100%',
                    textAlign: 'center',
                    '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' }
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ color: '#5C005D', mb: 2 }}>
                        {iconMap[item.icon] || <MusicIcon sx={{ fontSize: 40 }} />}
                      </Box>
                      <Typography variant="h6" sx={{ color: '#5C005D', mb: 1.5, fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Packages Section */}
        {service.packages && (
          <Box sx={{ py: 8, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                Our Packages
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
                Choose the perfect package for your celebration
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                {service.packages.map((pkg, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Card sx={{
                      height: '100%',
                      position: 'relative',
                      border: pkg.popular ? '2px solid #ffd700' : '1px solid #e0e0e0',
                      '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 6 }
                    }}>
                      {pkg.popular && (
                        <Chip
                          label="Most Popular"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#ffd700',
                            color: '#1a0a2e',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h5" align="center" sx={{ color: '#5C005D', mb: 2, fontWeight: 'bold' }}>
                          {pkg.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3, lineHeight: 1.7 }}>
                          {pkg.description}
                        </Typography>
                        <List dense>
                          {pkg.features.map((feature, j) => (
                            <ListItem key={j} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color: '#5C005D', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          variant={pkg.popular ? "contained" : "outlined"}
                          fullWidth
                          onClick={scrollToBooking}
                          color={pkg.popular ? "gold" : "primary"}
                          sx={{
                            mt: 2,
                            ...(pkg.popular
                              ? {
                                boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                              }
                              : {
                                borderColor: '#5C005D',
                                color: '#5C005D',
                                '&:hover': { borderColor: '#7a007b', backgroundColor: 'rgba(92, 0, 93, 0.04)' }
                              })
                          }}
                        >
                          Get Quote
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Wedding Types / Use Cases */}
        {service.weddingTypes && (
          <Box sx={{ py: 8, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                Every Wedding, Every Tradition
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
                Experienced performers for all South Asian wedding traditions
              </Typography>

              <Grid container spacing={4}>
                {service.weddingTypes.map((wt, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Paper sx={{ p: 4, height: '100%' }}>
                      <Typography variant="h5" sx={{ color: '#5C005D', mb: 2, fontWeight: 'bold' }}>
                        {wt.type}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {wt.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* How It Works */}
        {service.process && (
          <Box sx={{ py: 8, background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)', color: 'white' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: 'white', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                How It Works
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: 'rgba(255,255,255,0.8)', mb: 6, maxWidth: 700, mx: 'auto' }}>
                Three simple steps to unforgettable entertainment
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                {service.process.map((step, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: '#ffd700',
                        color: '#1a0a2e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}>
                        {step.step}
                      </Box>
                      <Typography variant="h5" sx={{ color: 'white', mb: 1.5 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'rgba(255,255,255,0.85)' }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* FAQ Section */}
        {service.faqs && (
          <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="md">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                Frequently Asked Questions
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6 }}>
                Everything you need to know about our {service.title.toLowerCase()} service
              </Typography>

              {service.faqs.map((faq, i) => (
                <Accordion
                  key={i}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    boxShadow: 1,
                    borderRadius: '8px !important',
                    overflow: 'hidden'
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#5C005D' }} />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5C005D' }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#444' }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Container>
          </Box>
        )}

        {/* Related Services */}
        {service.relatedServices && (
          <Box sx={{ py: 6, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <Typography variant="h4" align="center" sx={{ color: '#5C005D', mb: 4 }}>
                Explore Our Other Services
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                {service.relatedServices.map((related) => (
                  <Grid item xs={12} sm={4} key={related.slug}>
                    <Card
                      component={Link}
                      to={`/services/${related.slug}`}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <MusicIcon sx={{ color: '#5C005D', fontSize: 32, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#5C005D' }}>
                          {related.name}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5C005D' }}>
                          <Typography variant="body2">Learn More</Typography>
                          <ArrowIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Booking Form */}
        <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default ServicePage;
